import React from "react";
import ReactDOMServer from "react-dom/server";

import { faChevronRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import fastdom from "fastdom";
import { sortBy, throttle } from "lodash";
import { NodeType, Node as ProseNode, Slice } from "prosemirror-model";
import { EditorState, Plugin } from "prosemirror-state";
import { Decoration, DecorationSet, EditorView } from "prosemirror-view";
import uuid4 from "uuid/v4";

import { dispatchAnalyticsTrack } from "~/lib/segment";

import { Extension } from "../extension";
import {
	IMAGE_STRIP_MAX_CAPACITY,
	ImageStrip,
	insertImageStrip,
	isStripFull,
	moveItemFromStrip,
} from "../nodes/image-strip";
import { getPosAfterSection, isCollapsed } from "./collapse";
import { getContext } from "./context";
import type { ViewDesc } from "./mousePos";

interface MultiDragEvent {
	x: number;
	y: number;
	clientX: number;
	clientY: number;
	target: Element;
}

export abstract class DropTargetHandler {
	updateIndicator?: (indicator: HTMLElement, doc: ProseNode, view: EditorView, dropTarget: DropTarget) => void;
	abstract get type(): string;
	canHandle(_event: DragEvent): boolean {
		return true;
	}
	onDragEnter?: (view: EditorView, event: Event, dropTarget: DropTarget) => void;
	onDragOver?: (view: EditorView, event: Event, dropTarget: DropTarget) => void;
	onDragLeave?: (view: EditorView, event: Event, dropTarget: DropTarget) => void;
	validateDropPos(
		_pos: number,
		_doc: ProseNode,
		_view: EditorView,
		_desc: ViewDesc,
		_draggingFilesType: { kind: string; type: string }[],
	): { valid: boolean; skip: boolean } {
		return { valid: true, skip: false };
	}
	updateDecoration?: (state: EditorState, pluginState: DropTargetPluginSpec, dropTarget: DropTarget) => Decoration[];
	abstract onDrop(event: DragEvent, dropPos: number, view: EditorView, dropTarget: DropTarget): boolean;
}

export class FellowAttachmentHandler extends DropTargetHandler {
	upload: (attachments: { id: string; filename: string }[]) => void = () => undefined;
	get type() {
		return "fellow/attachment";
	}
	updateIndicator = (el: HTMLElement, _doc: ProseNode, view: EditorView, dropTarget: DropTarget) => {
		// Dropping a file has no indicator
		// Unless image_strip is enabled
		const imageStripExt = (view.extensions.get("image_strip") as ImageStrip) || undefined;
		if (imageStripExt) {
			if (dropTarget.referenceDesc?.node?.type.name === "image_strip_item") {
				el.classList.add("over-image-strip");
			} else {
				el.classList.remove("over-image-strip");
			}
		} else {
			while (el.lastChild) el.removeChild(el.lastChild);
		}
	};
	validateDropPos = (pos: number, doc: ProseNode, view: EditorView, desc: ViewDesc) => {
		const imageStripExt = (view.extensions.get("image_strip") as ImageStrip) || undefined;
		if (!imageStripExt) return { valid: true, skip: false };

		if (desc?.node && desc.node.type.name === "image_strip_item" && desc.parent?.node) {
			const { isFull } = isStripFull(desc.parent?.node, imageStripExt);
			return {
				valid: !isFull,
				skip: isFull,
			};
		}
		return {
			valid: true,
			skip: false,
		};
	};
	onDrop(e: DragEvent, _dropPos: number, view: EditorView, dropTarget: DropTarget) {
		// console.trace("attachmentdrop", _dropPos, dropTarget);
		e.preventDefault();
		e.stopPropagation();
		if (e.dataTransfer && e.dataTransfer.getData("fellow/attachment")) {
			const imageStripExt = (view.extensions.get("image_strip") as ImageStrip) || undefined;
			if (!imageStripExt) return false;
			const { id, dimensions } = JSON.parse(e.dataTransfer.getData("fellow/attachment")) as {
				id: string;
				filename: string;
				dimensions?: { height: number | undefined; width: number | undefined };
			};
			if (!id) return false;
			let imageStripNodePos = undefined;
			const refNode = dropTarget.referenceDesc?.node;
			const refStart = dropTarget.referenceDesc?.posAtStart;
			if (refNode && refStart && refNode.type.name === "image_strip_item") {
				// existing image_strip
				imageStripNodePos = dropTarget.dropPos;
			}
			if (dropTarget.dropPos !== null && !imageStripNodePos) {
				const insertPos = dropTarget.dropPos;
				insertImageStrip(insertPos)(view.state, view.dispatch);
				imageStripNodePos = insertPos + 1;
			}
			if (imageStripNodePos) {
				if (dimensions) {
					imageStripExt.setAttachmentDimensions(id, dimensions);
				}
				imageStripExt.linkAttachment(id, imageStripNodePos);
			}
		}
		return true;
	}
	updateDecoration = (state: EditorState, pluginState: DropTargetPluginSpec, dropTarget: DropTarget) => {
		if (!pluginState) return [];
		const decorations = [];
		const desc = pluginState.referenceDesc;
		if (desc && desc.node?.type.name === "image_strip_item") {
			const parentDesc = desc.parent;
			if (parentDesc) {
				const targetIndex = state.doc.resolve(desc.posAtStart).index(1);
				let insertIndex;
				if (pluginState.dropPos === desc.posBefore) insertIndex = targetIndex; // insert before the target
				if (pluginState.dropPos === desc.posAfter) insertIndex = targetIndex + 1; // insert after the target
				if (typeof insertIndex === undefined) return [];
				decorations.push(
					Decoration.node(parentDesc.posBefore, parentDesc.posAfter, {
						"data-dragging-over": "true",
						"data-dragging-over-invalid": `${!dropTarget.dropPosValid}`,
						"data-insert-index": `${insertIndex}`,
					}),
				);
			}
		}
		return decorations;
	};
}

// The class to store the dragging item's info
class ImageStripItemDragSource {
	view: EditorView | null = null;
	slice: Slice | null = null;
	startPos: number | null = null;
	endPos: number | null = null;
	itemIndex: number | null = null;
	lastItem: boolean | null = null;
	stripId: string | null = null;
	dragging = false;

	set({
		view,
		slice,
		startPos,
		endPos,
		itemIndex,
		lastItem,
		stripId,
	}: {
		view: EditorView;
		slice: Slice;
		startPos: number;
		endPos: number;
		itemIndex: number;
		lastItem: boolean;
		stripId: string;
	}) {
		this.view = view;
		this.slice = slice;
		this.startPos = startPos;
		this.endPos = endPos;
		this.itemIndex = itemIndex;
		this.lastItem = lastItem;
		this.stripId = stripId;
		this.dragging = true;
	}
	get() {
		if (
			this.view === null ||
			this.slice === null ||
			this.startPos === null ||
			this.endPos === null ||
			this.itemIndex === null ||
			this.lastItem === null ||
			this.stripId === null
		)
			return null;
		return {
			view: this.view,
			slice: this.slice,
			startPos: this.startPos,
			endPos: this.endPos,
			itemIndex: this.itemIndex,
			lastItem: this.lastItem,
			stripId: this.stripId,
		};
	}
	clear() {
		this.view = null;
		this.slice = null;
		this.startPos = null;
		this.endPos = null;
		this.itemIndex = null;
		this.lastItem = null;
		this.stripId = null;
	}
}

export const ImageStripItemDragSourceSingleton = new ImageStripItemDragSource();

export class ImageStripItemHandler extends DropTargetHandler {
	upload: (attachments: { id: string; filename: string }[]) => void = () => undefined;
	get type() {
		return "fellow/image-strip-item";
	}
	updateIndicator = (el: HTMLElement, _doc: ProseNode, view: EditorView, dropTarget: DropTarget) => {
		// Dropping a file has no indicator
		// Unless image_strip is enabled
		const imageStripExt = (view.extensions.get("image_strip") as ImageStrip) || undefined;
		if (imageStripExt) {
			if (dropTarget.referenceDesc?.node?.type.name === "image_strip_item") {
				el.classList.add("over-image-strip");
			} else {
				el.classList.remove("over-image-strip");
			}
		} else {
			while (el.lastChild) el.removeChild(el.lastChild);
		}
	};

	isDroppingInSameStrip = (sourcePos: number, targetPos: number, doc: ProseNode) => {
		const fromPos = doc.resolve(sourcePos);
		const toPos = doc.resolve(targetPos);
		if (fromPos.parent.type.name === "image_strip" && toPos.parent.type.name === "image_strip") {
			return fromPos.parent.attrs["blockId"] === toPos.parent.attrs["blockId"];
		}
		return false;
	};

	isSamePlaceInStrip = (sourcePos: number, targetPos: number, slice: Slice) => {
		if (sourcePos === targetPos) return true;
		if (sourcePos + slice.content.size === targetPos) return true;
		return false;
	};

	validateDropPos = (pos: number, doc: ProseNode, view: EditorView, desc: ViewDesc) => {
		const imageStripExt = (view.extensions.get("image_strip") as ImageStrip) || undefined;
		if (!imageStripExt) return { valid: true, skip: false };

		const dragSource = ImageStripItemDragSourceSingleton.get();
		if (!dragSource) return { valid: true, skip: false };

		const { view: sourceView, slice, startPos } = dragSource;
		const itemSlice = Slice.fromJSON(view.state.schema, slice);
		if (view === sourceView && this.isDroppingInSameStrip(startPos, pos, doc)) {
			if (this.isSamePlaceInStrip(startPos, pos, itemSlice)) {
				return { valid: false, skip: false };
			}
			return { valid: true, skip: false };
		}

		if (desc?.node && desc.node.type.name === "image_strip_item" && desc.parent?.node) {
			const { isFull } = isStripFull(desc.parent.node, imageStripExt);
			return {
				valid: !isFull,
				skip: isFull,
			};
		}
		return {
			valid: true,
			skip: false,
		};
	};
	onDrop(e: DragEvent, _dropPos: number, view: EditorView, dropTarget: DropTarget) {
		// console.trace("attachmentdrop", _dropPos, dropTarget);
		e.preventDefault();
		e.stopPropagation();

		const imageStripExt = (view.extensions.get("image_strip") as ImageStrip) || undefined;
		if (!imageStripExt) return false;

		const dragSource = ImageStripItemDragSourceSingleton.get();
		if (!dragSource) return false;

		const { view: sourceView, slice, lastItem } = dragSource;
		let { startPos, endPos } = dragSource;
		const itemSlice = Slice.fromJSON(view.state.schema, slice);
		if (!itemSlice || !itemSlice.content) return false;

		let imageStripNodePos = undefined;
		const refNode = dropTarget.referenceDesc?.node;
		const refStart = dropTarget.referenceDesc?.posAtStart;
		if (refNode && refStart && refNode.type.name === "image_strip_item") {
			// existing image_strip
			imageStripNodePos = dropTarget.dropPos;
		}
		if (dropTarget.dropPos !== null && !imageStripNodePos) {
			const insertPos = dropTarget.dropPos;
			insertImageStrip(insertPos)(view.state, view.dispatch);
			imageStripNodePos = insertPos + 1;
			if (view === sourceView && insertPos < startPos) {
				// if a new image strip is inserted before the position of the item that's being dragged,
				// we need to manually map the position forward so that we can delete the correct node later
				startPos += 2;
				endPos += 2;
			}
		}
		if (imageStripNodePos) {
			// do nothing if trying to insert at the same place
			if (sourceView === view && this.isSamePlaceInStrip(startPos, imageStripNodePos, itemSlice)) return false;
			// delete the whole strip if we are dragging out its only item
			const delStartPos = lastItem ? startPos - 1 : startPos;
			const delEndPos = lastItem ? endPos + 1 : endPos;
			moveItemFromStrip(imageStripNodePos, itemSlice.content, delStartPos, delEndPos)(view, sourceView);
		}
		return true;
	}
	updateDecoration = (state: EditorState, pluginState: DropTargetPluginSpec, dropTarget: DropTarget) => {
		if (!pluginState || !dropTarget.dropPosValid) return [];
		const decorations = [];
		const desc = pluginState.referenceDesc;
		if (desc && desc.node?.type.name === "image_strip_item") {
			const parentDesc = desc.parent;
			if (parentDesc) {
				const targetIndex = state.doc.resolve(desc.posAtStart).index(1);
				let insertIndex;
				if (pluginState.dropPos === desc.posBefore) insertIndex = targetIndex; // insert before the target
				if (pluginState.dropPos === desc.posAfter) insertIndex = targetIndex + 1; // insert after the target
				if (typeof insertIndex === undefined) return [];
				decorations.push(
					Decoration.node(parentDesc.posBefore, parentDesc.posAfter, {
						"data-dragging-over": "true",
						"data-dragging-over-invalid": `${!dropTarget.dropPosValid}`,
						"data-insert-index": `${insertIndex}`,
					}),
				);
			}
		}
		return decorations;
	};
}

export class FileDropHandler extends DropTargetHandler {
	upload: (files: File[]) => void;
	constructor(upload: (files: File[]) => void) {
		super();
		this.upload = upload;
	}
	get type() {
		return "Files";
	}
	resetIndicator = (dropTarget: DropTarget) => {
		if (dropTarget.dropTarget) {
			dropTarget.dropTarget.classList.remove("dropping-file");
			dropTarget.dropTarget.classList.remove("over-image-strip");
		}
	};
	updateIndicator = (el: HTMLElement, _doc: ProseNode, view: EditorView, dropTarget: DropTarget) => {
		// Dropping a file has no indicator
		// Unless image_strip is enabled
		const imageStripExt = (view.extensions.get("image_strip") as ImageStrip) || undefined;
		if (imageStripExt) {
			if (dropTarget.referenceDesc?.node?.type.name === "image_strip_item") {
				el.classList.add("over-image-strip");
			} else {
				el.classList.remove("over-image-strip");
			}
		} else {
			while (el.lastChild) el.removeChild(el.lastChild);
		}
	};
	validateDropPos = (
		_pos: number,
		_doc: ProseNode,
		view: EditorView,
		desc: ViewDesc,
		draggingFilesType: { kind: string; type: string }[],
	) => {
		const imageStripExt = (view.extensions.get("image_strip") as ImageStrip) || undefined;
		if (!imageStripExt) return { valid: true, skip: false };
		if (desc?.node && desc.node.type.name === "image_strip_item" && desc.parent?.node) {
			const { isFull, stripItemCount } = isStripFull(desc.parent.node, imageStripExt);
			const draggingFilesCount = draggingFilesType.length;
			const exceedCapacity = stripItemCount + draggingFilesCount > IMAGE_STRIP_MAX_CAPACITY;
			return {
				valid: !isFull && !exceedCapacity,
				skip: isFull || exceedCapacity,
			};
		}
		return {
			valid: true,
			skip: false,
		};
	};
	onDragOver = (view: EditorView, _event: Event, dropTarget: DropTarget) => {
		const imageStripExt = (view.extensions.get("image_strip") as ImageStrip) || undefined;
		if (!imageStripExt) {
			if (dropTarget.dropTarget && !dropTarget.dropTarget.classList.contains("dropping-file")) {
				dropTarget.dropTarget.classList.add("dropping-file");
			}
			return;
		}
	};
	onDragLeave = (_view: EditorView, _event: Event, dropTarget: DropTarget) => {
		this.resetIndicator(dropTarget);
	};
	onDrop(e: DragEvent, _dropPos: number, view: EditorView, dropTarget: DropTarget) {
		e.preventDefault();
		e.stopPropagation();
		// console.trace("filedrop");
		if (e.dataTransfer && e.dataTransfer.files.length) {
			const imageStripExt = (view.extensions.get("image_strip") as ImageStrip) || undefined;
			if (!imageStripExt) {
				this.upload(Array.from(e.dataTransfer.files).filter(f => !!f.type));
			} else {
				let imageStripNodePos = undefined;
				const refNode = dropTarget.referenceDesc?.node;
				const refStart = dropTarget.referenceDesc?.posAtStart;
				if (refNode && refStart && refNode.type.name === "image_strip_item") {
					// existing image_strip
					imageStripNodePos = dropTarget.dropPos;
				}
				if (dropTarget.dropPos !== null && !imageStripNodePos) {
					const insertPos = dropTarget.dropPos;
					insertImageStrip(insertPos)(view.state, view.dispatch);
					imageStripNodePos = insertPos + 1;
				}
				if (imageStripNodePos) {
					imageStripExt.uploadFiles(
						Array.from(e.dataTransfer.files).filter(f => !!f.type),
						imageStripNodePos,
					);
				}
			}
		}
		this.resetIndicator(dropTarget);
		window.setTimeout(() => view.focus(), 0);
		return true;
	}
	updateDecoration = (state: EditorState, pluginState: DropTargetPluginSpec, dropTarget: DropTarget) => {
		if (!pluginState) return [];
		const decorations = [];
		const desc = pluginState.referenceDesc;
		if (desc && desc.node?.type.name === "image_strip_item") {
			const parentDesc = desc.parent;
			if (parentDesc) {
				const targetIndex = state.doc.resolve(desc.posAtStart).index(1);
				let insertIndex;
				if (pluginState.dropPos === desc.posBefore) insertIndex = targetIndex; // insert before the target
				if (pluginState.dropPos === desc.posAfter) insertIndex = targetIndex + 1; // insert after the target
				if (typeof insertIndex === undefined) return [];
				decorations.push(
					Decoration.node(parentDesc.posBefore, parentDesc.posAfter, {
						"data-dragging-over": "true",
						"data-dragging-over-invalid": `${!dropTarget.dropPosValid}`,
						"data-insert-index": `${insertIndex}`,
					}),
				);
			}
		}
		return decorations;
	};
}

export class NoteTaskDropHandler extends DropTargetHandler {
	get type() {
		return "json/fellow/note-task";
	}
	validateDropPos(pos: number, _doc: ProseNode, _view: EditorView) {
		if (pos === null) return { valid: true, skip: true };
		// We know the inserted type is todo
		// using insertPoint(doc, pos, view.state.schema.nodes.todo) === pos is generally correct but slower
		return { valid: pos >= 0, skip: true };
	}
	onDrop(event: DragEvent, dropPos: number, view: EditorView) {
		// console.trace("notetaskdrop");
		try {
			const data = JSON.parse(((event as DragEvent).dataTransfer as DataTransfer).getData("json/fellow/note-task"));
			// TODO: Add support for multiple assignee once we have it
			const { id, text, done, assigneeId, analytics, dueDate } = data;
			const {
				state: { schema },
			} = view;
			const node = (view.state.schema.nodes["todo"] as NodeType).create(
				{
					blockId: uuid4(),
					done,
					originalBlockId: id,
					// TODO: Add support for multiple assignee once we have it
					assignee: assigneeId,
					dueDate,
				},
				schema.text(text),
			);
			const pos = dropPos;
			view.dispatch(view.state.tr.insert(pos, node));
			event.stopPropagation();
			event.preventDefault();
			/* eslint-disable camelcase */
			dispatchAnalyticsTrack("Note Block Added", {
				...analytics,
				method: "drag_and_drop",
				drag_type: "external",
				// note_id: null,
				// note_type: "",
				note_block_key: node.attrs.blockId,
				note_block_type: node.type.name,
				// TODO: Add support for multiple assignee once we have it
				note_block_assignee_id: assigneeId,
				note_block_done: done,
				original_note_block_id: id,
			});
			/* eslint-enable camelcase */
			return true;
		} catch (error) {
			return false;
		}
	}
}

function normalizeCursor(e: TouchEvent | MouseEvent): MultiDragEvent {
	if (e instanceof MouseEvent) {
		const target = e.target as Element;
		return {
			x: e.pageX,
			y: e.pageY,
			clientX: e.clientX,
			clientY: e.clientY,
			target: target,
		};
	} else {
		const touch = e.touches[0];
		const target = touch.target as Element;
		return {
			x: touch.pageX,
			y: touch.pageY,
			clientX: touch.clientX,
			clientY: touch.clientY,
			target: target,
		};
	}
}

const dragEventTypeGuard = (event: unknown) => {
	if ("DragEvent" in window && event instanceof DragEvent) return true;
	if (!(typeof event === "object")) return false;
	if (event === null) return false;
	if (!("dataTransfer" in event)) return false;
	if ("clientX" in event && "clientY" in event) return true;
	return false;
};
const LEAVE_TIMEOUT = 100;
const MOUSE_OVER_THROTTLE_WAIT = 16; // we try to match requestAnimationFrame (without start/cancelation responsibility)

type DropTargetPluginSpec = {
	referenceDesc?: ViewDesc;
	dropPos?: number;
};
class DropTarget extends Extension {
	dropTarget: HTMLElement | null;
	dropPos: number | null;
	dropPosValid: boolean;
	referenceDesc: ViewDesc | null;
	decoration: DecorationSet | null;
	leaveTimeout: null | number;
	handlers: { [key: string]: DropTargetHandler };
	lastHandler: DropTargetHandler | null;
	draggingFilesType: { kind: string; type: string }[];
	constructor(options = {}) {
		super(options);
		this.handlers = {};
		this.options.handlers.forEach((h: DropTargetHandler) => (this.handlers[h.type] = h));
		this.dropTarget = null;
		this.dropPos = null;
		this.dropPosValid = false;
		this.referenceDesc = null;
		this.decoration = null;
		this.leaveTimeout = null;
		this.lastHandler = null;
		this.draggingFilesType = [];
	}
	get name() {
		return "DropTarget";
	}
	get defaultOptions() {
		return {
			dropIndicatorClass: "prose-drop-indicator",
			getDropTarget: (dom: HTMLElement) => dom, //.closest(".notes-stream-card"),
			dropTargetActiveClass: "drop-target-active",
		};
	}
	get plugins() {
		const extension = this;
		// @ts-ignore
		const dropTargetPlugin = new Plugin<DropTargetPluginSpec>({
			filterTransaction: tr => {
				if (extension.dropPos !== null) {
					extension.dropPos = tr.mapping.map(extension.dropPos);
				}
				return true;
			},
			state: {
				init: () => {
					return {};
				},
				apply: (tr, value) => {
					const meta = tr.getMeta("setReferenceDesc");
					if (meta) {
						const { referenceDesc: newRefDesc, dropPos: newDropPos } = meta;
						let newValue = { ...value };
						if (typeof newRefDesc !== "undefined" && newRefDesc !== value.referenceDesc) {
							newValue = { ...newValue, referenceDesc: newRefDesc };
						}
						if (typeof newDropPos !== "undefined" && newDropPos !== value.dropPos) {
							newValue = { ...newValue, dropPos: newDropPos };
						}
						return newValue;
					}
					return value;
				},
			},
			view: (editorView: EditorView) => {
				const dropInidicator = document.createElement("div");
				dropInidicator.classList.add(this.options.dropIndicatorClass);

				dropInidicator.style.position = "absolute";
				const chevron = document.createElement("div");
				chevron.classList.add("chevron");
				chevron.innerHTML = ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={faChevronRight} />);
				dropInidicator.appendChild(chevron);
				(editorView.dom.parentNode as Node).appendChild(dropInidicator);

				return {
					update: (v, _prevState) => {
						let visible = false;
						if (this.dropPos !== null && this.referenceDesc) {
							visible = true;
							const { referenceDesc, dropPos } = this;
							fastdom.measure(() => {
								const parentBox = v.dom.getBoundingClientRect();
								const referenceBox = (referenceDesc.dom as HTMLElement).getBoundingClientRect();
								const translateX = referenceBox.left - parentBox.left;
								const isAbove = dropPos <= referenceDesc.posAtStart;
								const top = isAbove ? referenceBox.top : referenceBox.bottom;
								fastdom.mutate(() => {
									dropInidicator.style.transform = `translate(${translateX}px, ${top - parentBox.top}px)`;
									dropInidicator.style.width = `${parentBox.width - translateX}px`;
								});
							});
							if (this.lastHandler && this.lastHandler.updateIndicator) {
								this.lastHandler.updateIndicator(dropInidicator, v.state.doc, v, this);
							}
						}

						const isActive = dropInidicator.classList.contains("active");
						if (visible && !isActive) {
							dropInidicator.classList.add("active");
						} else if (!visible && isActive) {
							dropInidicator.classList.remove("active");
						}

						fastdom.mutate(() => {
							if (!this.dropPosValid) {
								dropInidicator.classList.add("invalid");
							} else {
								dropInidicator.classList.remove("invalid");
							}
						});
					},
					destroy: () => {
						dropInidicator.remove();
					},
				};
			},
			props: {
				editable: _state => {
					// console.log("get editable", this.dropPos, this.editorView?.dragging);
					return !this.editorView?.dragging;
				},
				decorations: state => {
					const pluginState = dropTargetPlugin.getState(state);
					if (!!pluginState?.referenceDesc && !!pluginState?.dropPos) {
						if (this.lastHandler && typeof this.lastHandler.updateDecoration === "function") {
							const decos = this.lastHandler.updateDecoration(state, pluginState, this);
							return DecorationSet.create(state.doc, decos);
						}
					}
					return null;
				},
			},
		});
		return [dropTargetPlugin];
	}
	handleDragOver(view: EditorView, event: Event): boolean {
		if (this.dropTarget && !this.dropTarget.classList.contains(this.options.dropTargetActiveClass)) {
			const { dropTarget } = this;
			fastdom.mutate(() => dropTarget.classList.add(this.options.dropTargetActiveClass));
		}
		this._clearLeaveTimeout();
		const handler = this.getHandler(event);
		if (!handler) {
			this.setDropPos(null);
			return false;
		}
		if (handler.onDragOver) {
			handler.onDragOver(view, event, this);
		}
		this.updateDropPosFromEvent(view, event);
		return true;
	}
	onDragOver = (event: Event) => {
		// console.log("ondragover");
		if (!this.editorView || !this.dropTarget) return;
		if (getContext(this.editorView.state).locked) return;
		if (!this.editorView) return;
		if (this.handleDragOver(this.editorView, event)) {
			event.preventDefault();
			event.stopPropagation();
		}
	};
	// In order to tell if dragging multiple files to a strip will exceed the stirp's capacity limit,
	// we need a way to know how many files are being dragged, and since this piece of info can't be
	// accessed from dragover event because of security issue (see https://html.spec.whatwg.org/multipage/dnd.html#dndevents)
	// we are getting it from dragenter, which gives us some infos about the dragging items.
	// However dragging items are not the same as dragging files, so we need to filter out only the items which 'kind' is "file"
	onDragEnter = (event: Event) => {
		if (!this.editorView || !this.dropTarget) return;
		if (getContext(this.editorView.state).locked) return;
		if (!this.editorView) return;
		if (event && event instanceof DragEvent && event.dataTransfer) {
			event.preventDefault();
			event.stopPropagation();
			const items: { kind: string; type: string }[] = [];
			for (let item of event.dataTransfer.items) {
				if (item.kind === "file") {
					items.push({ kind: item.kind, type: item.type });
				}
			}
			this.draggingFilesType = items;
		}
	};
	handleDragLeave(view: EditorView, event: Event): boolean {
		this.leaveTimeout = window.setTimeout(() => {
			this.setDropPos(null);
			this.cancelDropPosUpdates();
			const handler = this.lastHandler;
			if (handler && handler.onDragLeave) {
				handler.onDragLeave(view, event, this);
			}
			if (this.dropTarget) {
				const { dropTarget } = this;
				fastdom.mutate(() => dropTarget.classList.remove(this.options.dropTargetActiveClass));
			}
			this._clearLeaveTimeout();
		}, LEAVE_TIMEOUT);
		return false;
	}
	onDragLeave = (event: Event) => {
		if (!this.editorView) return;
		if (this.handleDragLeave(this.editorView, event)) {
			event.preventDefault();
			event.stopPropagation();
		}
	};
	handleDrop(view: EditorView, event: Event): boolean {
		this.cancelDropPosUpdates();

		if (this.dropTarget) {
			const { dropTarget } = this;
			fastdom.mutate(() => {
				dropTarget.classList.remove(this.options.dropTargetActiveClass);

				// reinstall the event listener to attempt to stop the extra events from coming through
				this.dropTarget?.removeEventListener("dragover", this.onDragOver);
				this.dropTarget?.addEventListener("dragover", this.onDragOver);
			});
		}
		const handler = this.getHandler(event);
		if (!handler) {
			this.setDropPos(null);
			return false;
		}
		if (this.dropPos === null) return false;
		const dp = this.dropPos;

		const res = handler.onDrop(event as DragEvent, dp, view, this);
		this.setDropPos(null);
		return res;
	}
	onDrop = (event: DragEvent) => {
		// console.log("ondrop", event.dataTransfer);
		if (!this.editorView) return;
		if (this.handleDrop(this.editorView, event)) {
			event.preventDefault();
			event.stopPropagation();
		}
		this.draggingFilesType = [];
	};
	private _updateDropPosFastDomID: (() => void) | null = null;
	private cancelDropPosUpdates = () => {
		// console.trace("cancelDropPosUpdates");
		this.updateDropPosFromEvent.cancel();
		this._updateDropPosFastDomID && fastdom.clear(this._updateDropPosFastDomID);
	};
	_updateDropPosFromEvent = (view: EditorView, event: Event) => {
		if (!(event instanceof MouseEvent) && !(event instanceof TouchEvent)) return;
		this.cancelDropPosUpdates();
		this._updateDropPosFastDomID = fastdom.measure(() => {
			const { clientY, clientX } = normalizeCursor(event);
			const rectsPerDescItem: { [key: string]: ClientRect | DOMRect } = {};
			const getRect = (elem: HTMLElement, id?: string) => {
				if (id) {
					// there is a key to save it
					if (rectsPerDescItem[id]) {
						return rectsPerDescItem[id];
					}

					rectsPerDescItem[id] = elem.getBoundingClientRect();
					return rectsPerDescItem[id];
				}

				// no key to save it
				return elem.getBoundingClientRect();
			};

			const stripViewDesc: ViewDesc[] = [];

			// @ts-ignore
			const visibleDescs = view.docView.children.filter((item: ViewDesc) => {
				const rect = (item.dom as HTMLElement).getBoundingClientRect();
				const bottomBoundary = window.innerHeight || document.documentElement.clientHeight;
				const rightBoundary = window.innerWidth || document.documentElement.clientWidth;

				if (item.node?.type.name === "image_strip") {
					// for image strip, we want to allow to drop into it even if the block is not
					// fully inside the viewport, so if half the strip is visible, we think that's
					// enough to drop items into it.
					const isVisibleEnoughToDrop =
						rect.bottom - rect.height / 2 >= 0 &&
						rect.left >= 0 &&
						rect.top + rect.height / 2 <= bottomBoundary &&
						rect.right <= rightBoundary;
					if (isVisibleEnoughToDrop && item.node?.attrs.blockId) {
						rectsPerDescItem[item.node.attrs.blockId] = rect;
						stripViewDesc.push(item);
					}
				}

				const isItemVisible =
					// checking if item is visible in viewport
					rect.top >= 0 && rect.left >= 0 && rect.bottom <= bottomBoundary && rect.right <= rightBoundary;

				if (isItemVisible && item.node?.attrs.blockId) {
					// just to avoid calling getBoundingClientRect() again
					rectsPerDescItem[item.node.attrs.blockId] = rect;
				}
				return isItemVisible;
			});

			stripViewDesc
				// @ts-ignore
				.reduce((children, strip) => [...children, ...strip.children], [])
				// @ts-ignore
				.forEach(item => visibleDescs.push(item));

			// This only handles top-level blocks and image_strip_item
			const descs = sortBy<ViewDesc>(visibleDescs, item => {
				const rect = getRect(item.dom as HTMLElement, item.node?.attrs.blockId);
				if (clientY > rect.top && clientY < rect.bottom) {
					return item.node?.type.name === "image_strip_item"
						? -9999 + Math.abs(rect.left - clientX)
						: // when hanging over the strip item we want it has the highest priority
							-1;
				}
				return Math.abs(rect.top - clientY);
			});

			for (let index = 0; index < descs.length; index++) {
				const desc = descs[index];
				const targetDOM = desc.dom as HTMLElement;
				let dropPos = desc.posBefore;
				const rect = getRect(targetDOM, desc.node?.attrs.blockId);

				if (clientY > rect.top + 0.5 * rect.height) {
					if (desc.node && isCollapsed(view.state, desc.node)) {
						dropPos = getPosAfterSection(view.state, desc.node);
					} else {
						dropPos = desc.posAfter;
					}
				}
				if (desc.node?.type.name === "image_strip_item") {
					if (clientX > rect.left + 0.5 * rect.width) {
						dropPos = desc.posAfter;
					} else {
						dropPos = desc.posBefore;
					}
				}
				if (this.lastHandler) {
					const { valid, skip } = this.lastHandler.validateDropPos(
						dropPos,
						view.state.doc,
						view,
						desc,
						this.draggingFilesType,
					);
					if (valid) {
						this.setDropPos(dropPos, desc);
					} else if (skip) {
						continue;
					} else {
						this.setDropPos(dropPos, desc, false);
					}
					return;
				}
				this.setDropPos(dropPos, desc);
				return;
			}
		});
	};
	updateDropPosFromEvent = throttle(this._updateDropPosFromEvent, MOUSE_OVER_THROTTLE_WAIT);

	setDropPos = (dp: number | null, referenceNode: ViewDesc | null = null, valid = true) => {
		if (!this.editorView) return;
		const { editorView: view } = this;
		if (this.dropPos === dp && valid === this.dropPosValid && this.referenceDesc === referenceNode) return;
		this.dropPos = dp;
		this.referenceDesc = referenceNode;
		this.dropPosValid = valid;

		view.dispatch(
			view.state.tr.setMeta("setReferenceDesc", { referenceDesc: this.referenceDesc, dropPos: this.dropPos }),
		);
	};
	_clearLeaveTimeout = () => {
		if (this.leaveTimeout !== null) {
			clearTimeout(this.leaveTimeout);
		}
	};
	getHandler = (event: unknown) => {
		if (!this.editorView || !dragEventTypeGuard(event)) return null;
		const e = event as DragEvent;
		if (!e.dataTransfer) return null;
		const types = e.dataTransfer.types;
		for (let index = 0; index < types.length; index++) {
			const type = types[index];
			if (this.handlers[type] && this.handlers[type].canHandle(e)) {
				const handler = this.handlers[type];
				this.lastHandler = handler;
				return handler;
			}
		}
		this.lastHandler = null;
		return null;
	};
	init() {
		const { editorView } = this;
		if (!editorView) return;
		const dropTarget = this.options.getDropTarget(editorView.dom) as HTMLElement | null;
		if (!dropTarget) {
			// console.warn("cannot find dropTarget");
			return;
		}
		this.dropTarget = dropTarget;
		dropTarget.addEventListener("dragenter", this.onDragEnter);
		dropTarget.addEventListener("dragover", this.onDragOver);
		dropTarget.addEventListener("dragleave", this.onDragLeave);
		dropTarget.addEventListener("drop", this.onDrop, { capture: true });
	}
	destroy() {
		const { editorView, dropTarget } = this;
		if (!editorView || !dropTarget) return;
		dropTarget.removeEventListener("dragenter", this.onDragEnter);
		dropTarget.removeEventListener("dragover", this.onDragOver);
		dropTarget.removeEventListener("dragleave", this.onDragLeave);
		dropTarget.removeEventListener("drop", this.onDrop, { capture: true });
	}
}

export default DropTarget;
